<template>
  <div class="filter-checkbox">
    <div v-if="filter.uiConfig?.showSearch" class="mb-2">
      <input 
        type="text"
        v-model="searchQuery"
        :placeholder="`Search ${filter.displayName.toLowerCase()}...`"
        class="w-full px-3 py-1 text-sm rounded-md border border-input bg-background text-foreground"
      />
    </div>
    
    <div class="space-y-2">
      <div 
        v-for="option in filteredOptions" 
        :key="option.value"
        class="flex items-center"
      >
        <input 
          type="checkbox"
          :id="`${filter.name}-${option.value}`"
          :value="option.value"
          :checked="isOptionSelected(option.value)"
          @change="toggleOption(option.value)"
          class="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        <label :for="`${filter.name}-${option.value}`" class="flex items-center justify-between w-full text-sm">
          <span>{{ option.displayValue }}</span>
          <span class="ml-1 text-xs text-muted-foreground">
            ({{ getOptionCount(option.value) }})
          </span>
        </label>
      </div>
    </div>
    
    <div v-if="hasMoreOptions" class="mt-2">
      <button 
        @click="showAllOptions = !showAllOptions"
        class="text-xs text-primary hover:underline"
      >
        {{ showAllOptions ? 'Show less' : `Show ${hiddenOptionsCount} more` }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useFilters, getFilterInteraction } from '~/layers/base/composables/useFilters'
import { useRoute } from 'vue-router'

const props = defineProps({
  filter: {
    type: Object,
    required: true
  },
  activeValue: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:value', 'clear'])

// const { getFilterOptionCount } = useFilters() // Temporarily disable count logic
// const filterInteraction = getFilterInteraction()

const searchQuery = ref('')
const showAllOptions = ref(false)

// Get count of vehicles for a filter option (Return 1 temporarily to bypass filtering)
const getOptionCount = (optionValue: string): number => {
   return 1; // TEMPORARY: Assume all options have at least 1 count
}

// Check if user has interacted with this filter
// const hasInteractedWithFilter = computed(() => { ... }); // Temporarily disable if not needed

// Get filter options with counts
const options = computed(() => {
  if (!props.filter || !props.filter.options || !Array.isArray(props.filter.options)) {
    return []
  }
  // console.log(`[FilterCheckbox - ${props.filter.name}] Received options:`, props.filter.options);
  
  // Ensure the mapped object has 'value' and 'displayValue'
  return props.filter.options.map((option: any) => {
    return {
         value: option.value, // Keep the original value
         displayValue: option.label || option.displayValue || option.value // Use label, fallback to displayValue or value
         // Add other properties from option if needed by other logic
    }
  })
})

// Filter options (TEMPORARILY BYPASSED)
const filteredOptions = computed(() => {
   console.log(`[FilterCheckbox - ${props.filter.name}] Bypassing filtering, returning all options:`, options.value);
   return options.value; // Return all options directly
   /* 
    let result = options.value
    
    if (searchQuery.value) {
      result = result.filter((option: any) => 
        option.displayValue.toLowerCase().includes(searchQuery.value.toLowerCase())
      )
    }
    
    // Temporarily disable count filter
    // result = result.filter((option: any) => getOptionCount(option.value) > 0)
    
    // Limit visible options unless "show more" is clicked
    const maxVisible = props.filter.uiConfig?.maxVisible || 10
    if (!showAllOptions.value && result.length > maxVisible) {
      return result.slice(0, maxVisible)
    }
    
    return result
    */
})

// Calculate if we have more options to show
const hasMoreOptions = computed(() => {
  const maxVisible = props.filter.uiConfig?.maxVisible || 10
  const totalOptions = options.value.filter((option: any) => {
    if (searchQuery.value) {
      return option.displayValue.toLowerCase().includes(searchQuery.value.toLowerCase())
    }
    return true
  }).length
  
  return totalOptions > maxVisible
})

// Count of hidden options
const hiddenOptionsCount = computed(() => {
  const maxVisible = props.filter.uiConfig?.maxVisible || 10
  const totalOptions = options.value.filter((option: any) => {
    if (searchQuery.value) {
      return option.displayValue.toLowerCase().includes(searchQuery.value.toLowerCase())
    }
    return true
  }).length
  
  return totalOptions - maxVisible
})

// Check if an option is selected
const isOptionSelected = (value: string) => {
  return Array.isArray(props.activeValue) && props.activeValue.includes(value)
}

// Toggle an option selection
const toggleOption = (value: string) => {
  if (!Array.isArray(props.activeValue)) {
    // Track interaction when first selecting an option
    // filterInteraction.trackFilterInteraction(props.filter.name)
    emit('update:value', [value])
    return
  }
  
  // Track interaction when modifying selection
  // filterInteraction.trackFilterInteraction(props.filter.name)
  
  // Always allow toggling any option
  if (isOptionSelected(value)) {
    const newValue = props.activeValue.filter(v => v !== value)
    emit('update:value', newValue.length ? newValue : undefined)
  } else {
    emit('update:value', [...props.activeValue, value])
  }
}

// Initialize component
onMounted(() => {
  console.log(`[FilterCheckbox] Initializing ${props.filter.name}`)
})
</script>
